// src/components/dfs/OpponentLineupModal.tsx
// REVISED: Use team abbreviation helper

import React, { useMemo } from 'react';
import { SubmittedLineupResponse } from '@/api/dfs';
import SafeViewportOverlay from '@/components/common/SafeViewportOverlay';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'; // Import types
import { useExitTransition } from '@/hooks/useExitTransition';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { getAbbreviation } from '@/utils/teams'; // Import the new helper
import { shouldMaskContestLineupPlayer } from '@/utils/contestLineupVisibility';

interface OpponentLineupModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineupData: SubmittedLineupResponse | null;
  username: string | null;
  /** True when the viewer opened their own row - title reads "Your Lineup". */
  isOwnLineup?: boolean;
  isLoading: boolean;
  error: string | null;
  onViewScoreBreakdown: (playerId: number) => void;
  onPlayerProfileClick: (playerId: number) => void;
}

// Define desired slot order
const DISPLAY_SLOT_ORDER: Record<string, number> = { 'FW': 1, 'MF': 2, 'DF': 3, 'FLEX': 4 };

const OpponentLineupModal: React.FC<OpponentLineupModalProps> = ({
  isOpen,
  onClose,
  lineupData,
  username,
  isOwnLineup = false,
  isLoading,
  error,
  onViewScoreBreakdown,
  onPlayerProfileClick,
}) => {
  useBodyScrollLock(isOpen);
  const { render, closing } = useExitTransition(isOpen);

  // Memoized sorting logic within the modal
  const sortedPlayers = useMemo(() => {
      if (!lineupData?.players) return [];
      return [...lineupData.players].sort((a, b) => {
          const orderA = DISPLAY_SLOT_ORDER[a.assigned_position] ?? 99;
          const orderB = DISPLAY_SLOT_ORDER[b.assigned_position] ?? 99;
          if (orderA !== orderB) return orderA - orderB;
          const salaryA = a.player?.dfs_salary ?? 0;
          const salaryB = b.player?.dfs_salary ?? 0;
          if (salaryA !== salaryB) return salaryB - salaryA;
          return (a.player?.id ?? 0) - (b.player?.id ?? 0);
      });
  }, [lineupData]);


  if (!render) {
    return null;
  }

  const displayUsername = username || `Opponent`;
  const titleText = isOwnLineup ? 'Your Lineup' : `${displayUsername}'s Lineup`;

  return (
    <SafeViewportOverlay
      onClose={onClose}
      closing={closing}
      className="items-start justify-center"
    >
      {/* Modal content container */}
      <div
        className={`relative flex min-h-0 max-h-full w-full max-w-md flex-col overflow-hidden rounded-lg border border-gray-700/50 bg-surface-card p-4 shadow-xl sm:max-h-[85dvh] sm:p-6 ${closing ? 'animate-scale-out' : 'animate-scale-in'}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={isOwnLineup ? 'Your lineup' : `${displayUsername}'s lineup`}
      >
        {/* Close button */}
        <button onClick={onClose} className="absolute right-1 top-1 z-10 flex h-11 w-11 items-center justify-center rounded-full text-2xl font-bold text-gray-400 transition-colors hover:bg-primary/50 hover:text-white active:bg-primary/50 active:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow" aria-label="Close lineup">×</button>

        {/* Modal Title */}
        <h2 className="mb-4 flex-shrink-0 px-10 text-center font-heading text-xl font-bold text-accent-light-blue">{titleText}</h2>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 scrollbar-mobile-hide">
          {/* Loading/Error States */}
          {isLoading && ( <p className="text-center text-gray-400 italic my-4">Loading lineup...</p> )}
          {error && !isLoading && ( <p className="text-center text-secondary my-4">{error}</p> )}

          {/* Lineup Data Display */}
          {lineupData && !isLoading && !error && (
            <>
              <p className="text-sm text-gray-400 mb-1 text-center"> Total Salary: ${lineupData.total_salary?.toLocaleString() ?? 'N/A'} </p>
              <p className="text-lg font-semibold text-accent-yellow mb-4 text-center"> Current Score: {lineupData.current_score?.toFixed(2) ?? '0.00'} pts </p>
              <div className="space-y-2 border-y border-gray-700 py-2">
              {sortedPlayers.map((p, idx) => {
                   const playerExists = p.player !== null && p.player !== undefined;
                   // The API is the privacy boundary for opponents. Keep a
                   // defensive client check, but never re-mask player data the
                   // API intentionally returned to its owner.
                   const isPlayerMasked = shouldMaskContestLineupPlayer(
                     isOwnLineup,
                     playerExists,
                     p.player?.match_time,
                   );

                   return (
                     <div key={p.player_id || `masked-${idx}`} className="flex justify-between items-center bg-primary/50 p-2 rounded text-sm space-x-2">
                        {/* Slot - Always visible */}
                        <span className="font-semibold w-8 text-center flex-shrink-0 text-accent-yellow/80 text-xs">{p.assigned_position}</span>

                        {isPlayerMasked ? (
                          <>
                            {/* Masked: Match not started — no salary, no name */}
                            <div className="flex-grow flex items-center overflow-hidden">
                              <span className="truncate text-gray-500 italic text-xs">Match not started</span>
                            </div>
                            <div className="flex items-center flex-shrink-0">
                              <span className="text-xs text-gray-500 w-14 text-right">--</span>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Revealed: Match started */}
                            <div className="flex-grow flex items-center space-x-1 overflow-hidden">
                              <button
                                type="button"
                                className={`min-w-0 truncate text-left font-medium ${playerExists ? 'text-gray-100 hover:text-accent-yellow hover:font-bold active:text-accent-yellow active:font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow' : 'text-gray-100'}`}
                                title={formatPlayerDisplayName(p.player)}
                                onClick={() => { if (playerExists && p.player) onPlayerProfileClick(p.player.id); }}
                                disabled={!playerExists}
                              >
                                {formatPlayerDisplayName(p.player)}
                              </button>
                              <span className="text-gray-400 text-xs flex-shrink-0" title={p.player?.current_team_name || ''}>({getAbbreviation(p.player?.current_team_name)})</span>
                            </div>
                            <div className="flex items-center space-x-3 flex-shrink-0">
                              <span className="text-xs text-gray-400 w-14 text-right">${(p.player?.dfs_salary ?? 0).toLocaleString()}</span>
                              {(() => {
                                const score = p.player?.current_score ?? 0.0;
                                const isClickable = playerExists && score !== 0;
                                return (
                                  <button
                                    type="button"
                                    className={`w-14 text-right text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow ${isClickable ? 'text-accent-yellow hover:underline active:underline' : 'text-gray-500'}`}
                                    onClick={() => { if (isClickable && p.player) onViewScoreBreakdown(p.player.id); }}
                                    title={isClickable ? "View score breakdown" : "Score: 0.00"}
                                    disabled={!isClickable}
                                  >
                                    {score.toFixed(2)} pts
                                  </button>
                                );
                              })()}
                            </div>
                          </>
                        )}
                     </div>
                  );
               })}
               {(sortedPlayers.length === 0) && ( <p className="text-gray-500 italic text-sm text-center">Lineup appears empty.</p> )}
              </div>
            </>
          )}
          {/* Fallback */}
          {!lineupData && !isLoading && !error && ( <p className="text-center text-gray-500 my-4">Lineup data unavailable.</p> )}
        </div>
      </div>
    </SafeViewportOverlay>
  );
};

export default OpponentLineupModal;
